import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { bleManager, BLEManager } from "../config/bleConfig";
import {
  Bluetooth, BluetoothOff, Radio, Zap, Battery, Gauge, Thermometer,
  Activity, AlertTriangle, CheckCircle, XCircle, ChevronDown,
  ChevronRight, Play, Terminal, Send, RotateCcw, Lock, Unlock,
  Car, Cpu, Signal, Clock, Hash
} from "lucide-react";

// Level colors
const LEVEL_STYLE = {
  STD: { bg: "rgba(45,212,168,0.08)", border: "rgba(45,212,168,0.25)", text: "#2dd4a8", label: "STD" },
  ADVANCED: { bg: "rgba(245,166,35,0.08)", border: "rgba(245,166,35,0.25)", text: "#f5a623", label: "ADV" },
  CRITICAL: { bg: "rgba(232,64,87,0.08)", border: "rgba(232,64,87,0.25)", text: "#e84057", label: "CRIT" },
};

// Animated pulse dot
const PulseDot = ({ color, size = 8 }) => (
  <span style={{ position: "relative", display: "inline-block", width: size, height: size }}>
    <span style={{
      position: "absolute", inset: 0, borderRadius: "50%", background: color,
      animation: "pulse 2s ease-in-out infinite"
    }} />
    <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color, opacity: 0.4,
      animation: "pulse 2s ease-in-out infinite", animationDelay: "0.3s", transform: "scale(1.8)"
    }} />
  </span>
);

export default function UnifiedDashboard() {
  // MCU state
  const [mcuStatus, setMcuStatus] = useState(null);
  const [mcuLoading, setMcuLoading] = useState(false);
  const [routines, setRoutines] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null);
  const [runningRoutine, setRunningRoutine] = useState(null);
  const [odinHistory, setOdinHistory] = useState([]);

  // ESP32 BLE state
  const [bleConnected, setBleConnected] = useState(false);
  const [bleData, setBleData] = useState(null);
  const [bleSupported] = useState(true); // Bridge is always supported (just WebSocket)

  // Command log
  const [cmdLog, setCmdLog] = useState([]);
  const logRef = useRef(null);

  // Raw CAN
  const [rawId, setRawId] = useState("0x7DF");
  const [rawData, setRawData] = useState(["02","01","00","00","00","00","00","00"]);

  // MCU raw command
  const [mcuEndpoint, setMcuEndpoint] = useState("/_data_get_value_request_");
  const [mcuPayload, setMcuPayload] = useState('{"name": "BMS_SOC"}');

  const addLog = useCallback((msg, type = "info") => {
    const ts = new Date().toLocaleTimeString();
    setCmdLog(prev => [...prev.slice(-100), { ts, msg, type }]);
  }, []);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [cmdLog]);

  // Setup BLE callbacks
  useEffect(() => {
    bleManager.onData((data) => {
      setBleData(data);
    });

    bleManager.onStatus((status) => {
      if (status.gateway_unlocked !== undefined) {
        addLog(`ESP32 Status: GW=${status.gateway_unlocked ? "Unlocked" : "Locked"} CAN=${status.can ? "Active" : "Off"}`, "ok");
      }
    });

    bleManager.onResponse((resp) => {
      addLog("ESP32 < " + JSON.stringify(resp), resp.error ? "err" : "ok");
    });

    bleManager.onDisconnect(() => {
      setBleConnected(false);
      addLog("ESP32 BLE disconnected", "err");
      toast.info("ESP32 BLE disconnected");
    });
  }, [addLog]);

  // Fetch MCU status
  const checkMCU = useCallback(async () => {
    setMcuLoading(true);
    try {
      const resp = await axios.get(`${API}/mcu/status`);
      setMcuStatus(resp.data);
      if (resp.data.reachable) {
        addLog(`MCU reachable at ${resp.data.ip} | SSH:${resp.data.ssh_open ? "Y" : "N"} | HTTP:${resp.data.http_open ? "Y" : "N"} | Factory:${resp.data.factory_mode ? "Y" : "N"}`, "ok");
      } else {
        addLog(`MCU unreachable at ${resp.data.ip} - Check Fakra cable`, "err");
      }
    } catch (e) {
      addLog("Failed to check MCU: " + (e.response?.data?.detail || e.message), "err");
    }
    setMcuLoading(false);
  }, [addLog]);

  // Fetch routines
  useEffect(() => {
    axios.get(`${API}/mcu/routines`).then(r => setRoutines(r.data)).catch(() => {});
    axios.get(`${API}/mcu/history?limit=20`).then(r => setOdinHistory(r.data)).catch(() => {});
  }, []);

  // ESP32 connect via BT bridge
  const connectESP32 = useCallback(async () => {
    try {
      addLog("Connecting to BT bridge (ws://localhost:8765)...", "cmd");
      await bleManager.connect();
      setBleConnected(true);
      addLog("ESP32 connected via BT bridge!", "ok");
      toast.success("ESP32 connected via Bluetooth bridge");
    } catch (e) {
      addLog("Bridge connect failed: " + e.message, "err");
      toast.error(e.message);
    }
  }, [addLog]);

  const disconnectESP32 = useCallback(async () => {
    bleManager.disconnect();
    setBleConnected(false);
    setBleData(null);
    addLog("ESP32 bridge disconnected", "info");
  }, [addLog]);

  const sendESP32Command = useCallback(async (cmdName) => {
    if (!bleConnected) {
      toast.error("ESP32 not connected");
      return;
    }
    addLog("ESP32 > " + cmdName, "cmd");
    bleManager.send({ cmd: cmdName });
  }, [bleConnected, addLog]);

  // Execute ODIN routine
  const executeODIN = useCallback(async (routine) => {
    setRunningRoutine(routine.id);
    addLog(`ODIN > ${routine.name} [${routine.level}]`, "cmd");
    try {
      const resp = await axios.post(`${API}/mcu/odin/${routine.id}`);
      addLog(`ODIN < ${routine.name}: ${JSON.stringify(resp.data).slice(0, 200)}`, "ok");
      toast.success(`${routine.name} executed`);
      const h = await axios.get(`${API}/mcu/history?limit=20`);
      setOdinHistory(h.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      addLog(`ODIN ERR: ${detail}`, "err");
      toast.error(detail);
    }
    setRunningRoutine(null);
  }, [addLog]);

  // Send raw MCU command
  const sendMCURaw = useCallback(async () => {
    try {
      const payload = JSON.parse(mcuPayload);
      addLog(`MCU > POST ${mcuEndpoint} ${JSON.stringify(payload)}`, "cmd");
      const resp = await axios.post(`${API}/mcu/proxy`, { endpoint: mcuEndpoint, payload });
      addLog(`MCU < [${resp.data.status}] ${JSON.stringify(resp.data.data || resp.data.raw).slice(0, 300)}`, "ok");
    } catch (e) {
      addLog(`MCU ERR: ${e.response?.data?.detail || e.message}`, "err");
    }
  }, [mcuEndpoint, mcuPayload, addLog]);

  // Send raw CAN via ESP32 bridge
  const sendRawCAN = useCallback(async () => {
    const id = parseInt(rawId);
    const data = rawData.map(v => parseInt(v, 16) || 0);
    bleManager.sendRawCAN(id, data);
    addLog(`CAN > ID:0x${id.toString(16).toUpperCase()} Data:[${data.map(b => b.toString(16).padStart(2,'0')).join(' ')}]`, "cmd");
  }, [rawId, rawData, addLog]);

  const fmt = (v, d = 1) => v != null ? Number(v).toFixed(d) : "--";

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1600, margin: "0 auto" }}>
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        .ucard { background:#0e0e16; border:1px solid #1a1a28; border-radius:10px; padding:20px; transition:border-color .2s; }
        .ucard:hover { border-color:#2a2a3e; }
        .ucard-title { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#6b7280; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
        .urow { display:flex; justify-content:space-between; align-items:baseline; padding:5px 0; border-bottom:1px solid rgba(255,255,255,0.03); }
        .urow:last-child { border:none; }
        .ulabel { color:#6b7280; font-size:12px; }
        .uval { font-size:15px; font-weight:600; font-variant-numeric:tabular-nums; color:#e2e8f0; }
        .uval-lg { font-size:26px; }
        .ubtn { font-size:11px; padding:7px 14px; border-radius:5px; border:1px solid #1e1e2e; background:#14141e; color:#e2e8f0; cursor:pointer; font-weight:600; letter-spacing:.5px; transition:all .15s; display:inline-flex; align-items:center; gap:6px; }
        .ubtn:hover { background:#1c1c2a; border-color:#3a3a4e; }
        .ubtn:active { transform:scale(.97); }
        .ubtn:disabled { opacity:0.4; cursor:not-allowed; }
        .ubtn-r { border-color:rgba(232,64,87,.3); color:#e84057; }
        .ubtn-r:hover { background:rgba(232,64,87,.08); }
        .ubtn-b { border-color:rgba(74,158,255,.3); color:#4a9eff; }
        .ubtn-b:hover { background:rgba(74,158,255,.08); }
        .ubtn-g { border-color:rgba(45,212,168,.3); color:#2dd4a8; }
        .ubtn-g:hover { background:rgba(45,212,168,.08); }
        .uinput { font-family:inherit; font-size:12px; padding:6px 10px; border-radius:5px; border:1px solid #1e1e2e; background:#0a0a10; color:#e2e8f0; }
        .ulog { background:#08080d; border:1px solid #1a1a28; border-radius:6px; padding:10px; font-size:11px; max-height:220px; overflow-y:auto; line-height:1.7; font-family:'SF Mono',Consolas,monospace; }
        .ulog .ok { color:#2dd4a8; } .ulog .err { color:#e84057; } .ulog .cmd { color:#4a9eff; } .ulog .info { color:#6b7280; }
        .soc-bar { width:100%; height:5px; background:#1a1a2a; border-radius:3px; overflow:hidden; margin:6px 0 2px; }
        .soc-fill { height:100%; border-radius:3px; transition:width .5s ease; }
        .cat-header { display:flex; align-items:center; gap:10px; padding:10px 14px; cursor:pointer; border-radius:8px; transition:background .15s; }
        .cat-header:hover { background:rgba(255,255,255,0.03); }
        .routine-item { display:flex; align-items:center; justify-content:space-between; padding:8px 14px 8px 38px; border-radius:6px; transition:background .15s; }
        .routine-item:hover { background:rgba(255,255,255,0.02); }
        .level-badge { font-size:9px; font-weight:700; padding:2px 6px; border-radius:3px; letter-spacing:1px; }
        .conn-card { display:flex; align-items:center; gap:14px; padding:16px; border-radius:10px; border:1px solid #1a1a28; background:#0e0e16; }
      `}</style>

      {/* Header Row - Connection Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        {/* MCU Connection */}
        <div className="conn-card" data-testid="mcu-connection-card">
          <div style={{ width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            background: mcuStatus?.reachable ? "rgba(45,212,168,0.1)" : "rgba(232,64,87,0.1)",
            border: `1px solid ${mcuStatus?.reachable ? "rgba(45,212,168,0.3)" : "rgba(232,64,87,0.2)"}`
          }}>
            <Car size={22} color={mcuStatus?.reachable ? "#2dd4a8" : "#e84057"} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280", letterSpacing: 1.5, textTransform: "uppercase" }}>Tesla MCU</div>
            <div style={{ fontSize: 14, color: "#e2e8f0", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              192.168.90.100
              {mcuStatus?.reachable && <PulseDot color="#2dd4a8" />}
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              {mcuStatus ? (
                <>SSH:{mcuStatus.ssh_open ? <span style={{color:"#2dd4a8"}}>Open</span> : <span style={{color:"#e84057"}}>Closed</span>}
                {" | "}8080:{mcuStatus.http_8080 ? <span style={{color:"#2dd4a8"}}>Open</span> : <span style={{color:"#e84057"}}>Closed</span>}
                {" | "}4070:{mcuStatus.http_4070 ? <span style={{color:"#2dd4a8"}}>Factory</span> : <span style={{color:"#6b7280"}}>Closed</span>}
                {mcuStatus.latency_ms ? <span style={{color:"#6b7280"}}> | {mcuStatus.latency_ms}ms</span> : null}</>
              ) : "Not checked"}
            </div>
          </div>
          <button className="ubtn ubtn-b" onClick={checkMCU} disabled={mcuLoading} data-testid="check-mcu-btn">
            {mcuLoading ? <RotateCcw size={13} className="animate-spin" /> : <Signal size={13} />}
            {mcuLoading ? "Scanning..." : "Check"}
          </button>
        </div>

        {/* ESP32 BLE Connection */}
        <div className="conn-card" data-testid="esp32-connection-card">
          <div style={{ width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            background: bleConnected ? "rgba(74,158,255,0.1)" : "rgba(255,255,255,0.05)",
            border: `1px solid ${bleConnected ? "rgba(74,158,255,0.3)" : "#1e1e2e"}`
          }}>
            <Bluetooth size={22} color={bleConnected ? "#4a9eff" : "#6b7280"} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280", letterSpacing: 1.5, textTransform: "uppercase" }}>ESP32 CAN (BT)</div>
            <div style={{ fontSize: 14, color: "#e2e8f0", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              TeslaDiag
              {bleConnected && <PulseDot color="#4a9eff" />}
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              {bleConnected && bleData ? (
                <>CAN: <span style={{color:"#2dd4a8"}}>Live</span>
                {" | "}Msgs: {bleData.msg?.toLocaleString() || 0}</>
              ) : "Bluetooth Classic SPP via bt_bridge.py"}
            </div>
          </div>
          {bleConnected ? (
            <button className="ubtn ubtn-r" onClick={disconnectESP32} data-testid="disconnect-esp32-btn">
              <BluetoothOff size={13} /> Disconnect
            </button>
          ) : (
            <button className="ubtn ubtn-g" onClick={connectESP32} data-testid="connect-esp32-btn">
              <Bluetooth size={13} /> Connect
            </button>
          )}
        </div>
      </div>

      {/* Main Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Battery Card */}
        <div className="ucard" data-testid="battery-card">
          <div className="ucard-title"><Battery size={14} color="#2dd4a8" /> HV Battery</div>
          <div className="urow"><span className="ulabel">State of Charge</span><span className="uval uval-lg" style={{color:"#2dd4a8"}}>{fmt(bleData?.soc)}%</span></div>
          <div className="soc-bar"><div className="soc-fill" style={{ width: `${bleData?.soc || 0}%`, background: (bleData?.soc||0) < 20 ? "#e84057" : (bleData?.soc||0) < 50 ? "#f5a623" : "#2dd4a8" }} /></div>
          <div className="urow"><span className="ulabel">Pack Voltage</span><span className="uval">{fmt(bleData?.v)} V</span></div>
          <div className="urow"><span className="ulabel">Pack Current</span><span className="uval">{fmt(bleData?.i)} A</span></div>
          <div className="urow"><span className="ulabel">Battery Temp</span><span className="uval">{fmt(bleData?.bt, 0)} C</span></div>
        </div>

        {/* Drive Card */}
        <div className="ucard" data-testid="drive-card">
          <div className="ucard-title"><Gauge size={14} color="#4a9eff" /> Drive Unit</div>
          <div className="urow"><span className="ulabel">Speed</span><span className="uval uval-lg" style={{color:"#4a9eff"}}>{fmt(bleData?.spd)} km/h</span></div>
          <div className="urow"><span className="ulabel">12V Battery</span><span className="uval" style={{color: (bleData?.["12v"]||13) < 11.8 ? "#e84057" : "#f5a623"}}>{fmt(bleData?.["12v"])} V</span></div>
          <div className="urow">
            <span className="ulabel">Gateway</span>
            <span className="uval" style={{ color: bleData?.gw ? "#2dd4a8" : "#e84057", display: "flex", alignItems: "center", gap: 4 }}>
              {bleData?.gw ? <><Unlock size={13} /> Unlocked</> : <><Lock size={13} /> Locked</>}
            </span>
          </div>
        </div>

        {/* System Card */}
        <div className="ucard" data-testid="system-card">
          <div className="ucard-title"><Zap size={14} color="#f5a623" /> Connection Info</div>
          <div className="urow"><span className="ulabel">MCU (Fakra)</span><span className="uval" style={{fontSize:12, color: mcuStatus?.reachable ? "#2dd4a8" : "#6b7280"}}>{mcuStatus?.reachable ? "Connected" : "Disconnected"}</span></div>
          <div className="urow"><span className="ulabel">ESP32 (BT)</span><span className="uval" style={{fontSize:12, color: bleConnected ? "#4a9eff" : "#6b7280"}}>{bleConnected ? "Connected" : "Disconnected"}</span></div>
          <div className="urow"><span className="ulabel">CAN Bus</span><span className="uval" style={{fontSize:12, color: bleData ? "#2dd4a8" : "#6b7280"}}>{bleData ? "Receiving" : "No Data"}</span></div>
          <div className="urow"><span className="ulabel">Mode</span><span className="uval" style={{fontSize:12}}>
            {mcuStatus?.reachable && bleConnected ? <span style={{color:"#2dd4a8"}}>Full Hardware</span> :
             mcuStatus?.reachable ? <span style={{color:"#f5a623"}}>Fakra Only</span> :
             bleConnected ? <span style={{color:"#4a9eff"}}>BLE Only</span> :
             <span style={{color:"#6b7280"}}>Offline</span>}
          </span></div>
        </div>
      </div>

      {/* ODIN Routines + Log */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* ODIN Routines */}
        <div className="ucard" style={{ maxHeight: 520, overflowY: "auto" }} data-testid="odin-routines-card">
          <div className="ucard-title"><Activity size={14} color="#e84057" /> ODIN Service Routines</div>
          {routines && Object.entries(routines).map(([key, cat]) => {
            const lvl = LEVEL_STYLE[cat.level] || LEVEL_STYLE.STD;
            return (
              <div key={key} style={{ marginBottom: 4 }}>
                <div className="cat-header" onClick={() => setExpandedCat(expandedCat === key ? null : key)}>
                  {expandedCat === key ? <ChevronDown size={14} color="#6b7280" /> : <ChevronRight size={14} color="#6b7280" />}
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0", flex: 1 }}>{cat.name}</span>
                  <span className="level-badge" style={{ background: lvl.bg, border: `1px solid ${lvl.border}`, color: lvl.text }}>{lvl.label}</span>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>{cat.routines.length}</span>
                </div>
                {expandedCat === key && cat.routines.map(r => {
                  const rl = LEVEL_STYLE[r.level] || LEVEL_STYLE.STD;
                  return (
                    <div className="routine-item" key={r.id}>
                      <div>
                        <div style={{ fontSize: 12, color: "#e2e8f0" }}>{r.name}</div>
                        <div style={{ fontSize: 10, color: "#6b7280" }}>{r.endpoint}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="level-badge" style={{ background: rl.bg, border: `1px solid ${rl.border}`, color: rl.text }}>{rl.label}</span>
                        <button
                          className={`ubtn ${r.level === "CRITICAL" ? "ubtn-r" : r.level === "ADVANCED" ? "ubtn-b" : "ubtn-g"}`}
                          onClick={() => {
                            if (r.level === "CRITICAL" && !window.confirm(`CRITICAL: Run "${r.name}"? This may affect vehicle operation.`)) return;
                            executeODIN(r);
                          }}
                          disabled={runningRoutine === r.id}
                          data-testid={`run-${r.id}`}
                        >
                          {runningRoutine === r.id ? <RotateCcw size={11} className="animate-spin" /> : <Play size={11} />}
                          Run
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Command Log + Raw */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Log */}
          <div className="ucard" style={{ flex: 1 }} data-testid="command-log-card">
            <div className="ucard-title"><Terminal size={14} color="#4a9eff" /> Command Log</div>
            <div className="ulog" ref={logRef}>
              {cmdLog.length === 0 && <div className="info">[ READY ] Waiting for commands...</div>}
              {cmdLog.map((l, i) => <div key={i} className={l.type}>[{l.ts}] {l.msg}</div>)}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="ucard" data-testid="quick-actions-card">
            <div className="ucard-title"><Send size={14} color="#f5a623" /> Quick Actions</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="ubtn ubtn-b" onClick={() => sendESP32Command("scan_dtc")} disabled={!bleConnected} data-testid="scan-dtc-btn"><AlertTriangle size={12} /> Scan DTC</button>
              <button className="ubtn ubtn-r" onClick={() => { if(window.confirm("Clear all DTCs?")) sendESP32Command("clear_dtc") }} disabled={!bleConnected} data-testid="clear-dtc-btn"><XCircle size={12} /> Clear DTC</button>
              <button className="ubtn ubtn-g" onClick={() => sendESP32Command("gateway")} disabled={!bleConnected} data-testid="unlock-gw-btn"><Unlock size={12} /> Unlock GW</button>
              <button className="ubtn" onClick={checkMCU} data-testid="ping-mcu-btn"><Signal size={12} /> Ping MCU</button>
            </div>

            {/* Raw CAN */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #1a1a28" }}>
              <div style={{ fontSize: 10, color: "#6b7280", letterSpacing: 1, marginBottom: 6 }}>RAW CAN (ESP32 BT)</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#6b7280" }}>ID:</span>
                <input className="uinput" style={{ width: 70 }} value={rawId} onChange={e => setRawId(e.target.value)} data-testid="raw-can-id" />
                {rawData.map((v, i) => (
                  <input key={i} className="uinput" style={{ width: 36, textAlign: "center" }} value={v}
                    onChange={e => { const n = [...rawData]; n[i] = e.target.value; setRawData(n); }}
                    data-testid={`raw-can-d${i}`} />
                ))}
                <button className="ubtn ubtn-b" onClick={sendRawCAN} disabled={!bleConnected} data-testid="send-raw-can-btn"><Send size={11} /> Send</button>
              </div>
            </div>

            {/* Raw MCU */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #1a1a28" }}>
              <div style={{ fontSize: 10, color: "#6b7280", letterSpacing: 1, marginBottom: 6 }}>RAW MCU HTTP (Fakra)</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input className="uinput" style={{ width: 220 }} value={mcuEndpoint} onChange={e => setMcuEndpoint(e.target.value)} data-testid="mcu-endpoint" />
                <input className="uinput" style={{ flex: 1, minWidth: 200 }} value={mcuPayload} onChange={e => setMcuPayload(e.target.value)} data-testid="mcu-payload" />
                <button className="ubtn ubtn-b" onClick={sendMCURaw} data-testid="send-mcu-raw-btn"><Send size={11} /> Send</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ODIN History */}
      {odinHistory.length > 0 && (
        <div className="ucard" data-testid="odin-history-card">
          <div className="ucard-title"><Clock size={14} color="#6b7280" /> Recent ODIN History</div>
          <div style={{ maxHeight: 180, overflowY: "auto" }}>
            {odinHistory.map((h, i) => (
              <div key={i} className="urow" style={{ fontSize: 12 }}>
                <span className="ulabel" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Hash size={10} /> {h.routine_name}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="level-badge" style={{ ...(LEVEL_STYLE[h.level] || LEVEL_STYLE.STD), background: LEVEL_STYLE[h.level]?.bg, border: `1px solid ${LEVEL_STYLE[h.level]?.border || "#1e1e2e"}`, color: LEVEL_STYLE[h.level]?.text || "#6b7280" }}>{h.level}</span>
                  <span style={{ color: h.status_code === 200 ? "#2dd4a8" : "#f5a623", fontFamily: "monospace" }}>{h.status_code}</span>
                  <span style={{ color: "#6b7280", fontSize: 10 }}>{new Date(h.timestamp).toLocaleString()}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
